import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideGrid,
  GuideCard,
  GuideDarkSection
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function WhoIsResponsibleGuide() {
  return (
    <GuideLayout
      slug="who-is-responsible"
      category="Getting Started"
      categoryColor="brand"
      title="Who Is Responsible for the Road?"
      description="How to identify whether it's your local council, National Highways, or another authority responsible for maintaining the road where you were damaged."
      readTime="4 min"
      updatedDate="2026-01-12"
      datePublished="2026-01-10"
      reviewedFor="UK law"
      sources={[
        { name: 'legislation.gov.uk', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/1' },
      ]}
      relatedGuides={[
        { slug: 'what-makes-a-valid-claim', title: 'What Makes a Valid Pothole Claim?', readTime: '5 min' },
        { slug: 'section-41-explained', title: 'Section 41 of the Highways Act', readTime: '6 min' },
        { slug: 'claim-timeline', title: 'How Long Does a Claim Take?', readTime: '3 min' },
      ]}
      atAGlance={[
        { label: 'Local roads, B roads, and residential streets', value: 'Maintained by your local council' },
        { label: 'Motorways and major trunk roads in England', value: 'Maintained by National Highways' },
        { label: 'Trunk roads in Scotland and Wales', value: 'Transport Scotland and the Welsh Government respectively' },
        { label: 'Private, unadopted roads, and car parks', value: 'The landowner is responsible — you cannot claim against the council' },
        { label: 'Pavements next to public roads', value: 'Maintained by the highway authority, usually your local council' },
      ]}
      faqs={[
        {
          question: 'How do I find out which council is responsible for a specific road?',
          answer: 'A postcode can suggest a likely authority, but boundaries do not prove who maintains a specific road. Verify the specific road manager using the authority\'s official highway records or the relevant strategic-road operator.',
        },
        {
          question: 'What if the pothole is on a motorway or major A-road?',
          answer: 'Motorways and major trunk roads in England are maintained by National Highways (formerly Highways England). In Scotland, Transport Scotland is responsible. You\'ll need to submit your claim to them rather than the local council.',
        },
        {
          question: 'Can I claim if the road isn\'t adopted?',
          answer: 'Unadopted roads are not maintained by the council, so you cannot claim against them. You would need to pursue the private landowner or management company responsible for the road. Many housing estates have unadopted roads during construction.',
        },
        {
          question: 'What about car parks and supermarket roads?',
          answer: 'Private car parks, including those at supermarkets, shopping centres, and hospitals, are the responsibility of the landowner, not the council. You would need to claim against the business or landowner directly.',
        },
        {
          question: 'Who maintains the pavement?',
          answer: 'Pavements (footpaths) adjacent to public roads are also maintained by the highway authority, usually your local council. You can claim for damage caused by pavement defects, including injuries from trips and falls.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        title="Understanding Road Responsibility"
      >
        <p className="mb-4">
          In the UK,{' '}
          <Link to="/authority-ratings" className={inlineLink}>
            different authorities
          </Link>
          {' '}are responsible for maintaining different types of roads. 
          Getting{' '}
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            your claim
          </Link>
          {' '}to the right authority is crucial — send it to the wrong one and it will 
          simply be{' '}
          <Link to="/guides/understanding-rejection-reasons" className={inlineLink}>
            rejected
          </Link>
          {' '}or ignored.
        </p>
        <GuideTip variant="info" title="Use the result as a lead">
          <Link to="/meet-mac" className={inlineLink}>Mac</Link>
          {' '}can suggest a likely authority from the location you provide. You still need to
          verify the specific road manager through official records before submitting a claim.
        </GuideTip>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
        title="Types of Highway Authorities"
      >
        <GuideGrid cols={2}>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
            title="Local Councils"
          >
            Responsible for most local roads, residential streets, and minor A and B roads within their area.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
            title="National Highways"
          >
            Maintains motorways and major trunk roads (strategic road network) in England.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
              </svg>
            }
            title="Transport Scotland"
          >
            Responsible for trunk roads and motorways in Scotland.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Welsh Government"
          >
            Maintains trunk roads in Wales, with local roads under council control.
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        }
        title="Quick Reference: Road Types"
      >
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-brand-900/50 rounded-xl">
            <span className="font-mono text-accent-400 font-bold">M</span>
            <div>
              <p className="text-white font-medium">Motorways (M1, M25, etc.)</p>
              <p className="text-brand-300 text-sm">National Highways in England, Transport Scotland in Scotland</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-brand-900/50 rounded-xl">
            <span className="font-mono text-accent-400 font-bold">A</span>
            <div>
              <p className="text-white font-medium">A Roads</p>
              <p className="text-brand-300 text-sm">Major trunk A roads: National Highways. Other A roads: Local council</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-brand-900/50 rounded-xl">
            <span className="font-mono text-accent-400 font-bold">B</span>
            <div>
              <p className="text-white font-medium">B Roads and Local Roads</p>
              <p className="text-brand-300 text-sm">Always maintained by the local council</p>
            </div>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
        title="Roads NOT Maintained by Councils"
      >
        <p className="mb-4">
          Some roads are not the responsibility of any highway authority. For these, you would need 
          to pursue the private landowner directly:
        </p>
        <ul className="space-y-2 mb-4">
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700">Private roads and driveways</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700">Unadopted roads (often in new developments)</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700">Car parks (supermarkets, shopping centres, hospitals)</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700">Industrial estate roads (unless adopted)</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700">Roads within private housing estates</span>
          </li>
        </ul>
        <GuideTip variant="warning" title="Not sure?">
          If you're unsure whether a road is publicly maintained, check with{' '}
          <Link to="/councils" className={inlineLink}>
            your local council
          </Link>
          {' '}or use Mac&apos;s postcode result as a likely-authority lead, then verify the
          specific road manager through official records.
        </GuideTip>
      </GuideSection>
      <GuideTip variant="info" title="Strategic-road incident?">
        Once you have confirmed National Highways maintained the road, follow the{' '}
        <Link to="/guides/national-highways-pothole-claims" className={inlineLink}>
          official Red Claim route guide
        </Link>
        . Welsh motorways and trunk roads use Welsh Government routes instead.
      </GuideTip>
    </GuideLayout>
  );
}
